/**
 * What you know, who you have played, and where you stand.
 *
 * Three reads over three rollups the room writes at match end. Nothing here
 * computes anything expensive: coverage is a join between codes the server
 * sent and the country table the client already holds to draw the globe.
 *
 * WHY COVERAGE IS BY REGION. "You have found 34 of 55 African countries" is a
 * sentence with a next step in it. A single number over two hundred is not:
 * it moves too slowly to feel like progress and it never tells you where to
 * go. The regions are also the shape the game already thinks in -- Explorer
 * pre-orients the globe to one.
 *
 * NOTHING IS SOLD HERE. Coverage is the retention hook, so it belongs to
 * everybody; see `docs/MONETISATION.md` §7.
 */

import { useEffect, useState } from 'react';
import { allCountries } from '@geogame/data';
import { GAME_MODES } from '@geogame/shared';
import type { GameMode } from '@geogame/shared';

import { fetchBoard, fetchCoverage, fetchRivals } from '../lib/api';
import type { BoardRow, CoverageEntity, Rival } from '../lib/api';
import { points } from '../lib/format';
import { navigate } from '../lib/router';
import { useGame } from '../store/gameStore';
import { identityToken } from '../store/storage';

const MODE_LABEL: Record<GameMode, string> = {
  country: 'Countries',
  city: 'Cities',
  state: 'US States',
};

/** Region -> every country in it. Built once; the table never changes. */
const BY_REGION = (() => {
  const out = new Map<string, string[]>();
  for (const c of allCountries()) {
    const list = out.get(c.region) ?? [];
    list.push(c.cca3);
    out.set(c.region, list);
  }
  return [...out.entries()].sort((a, b) => b[1].length - a[1].length);
})();

/** Codes this player has ever FOUND, which is what coverage claims. */
function foundSet(entities: readonly CoverageEntity[]): Set<string> {
  const out = new Set<string>();
  for (const e of entities) if (e.correct > 0) out.add(e.code);
  return out;
}

function Bar({ done, total }: { readonly done: number; readonly total: number }): React.JSX.Element {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div
      className="gg-cover-bar"
      role="img"
      aria-label={`${done} of ${total}`}
      style={{ ['--pct' as string]: `${pct}%` }}
    >
      <span className="gg-cover-fill" />
    </div>
  );
}

export function ProgressScreen(): React.JSX.Element {
  const account = useGame((s) => s.account);
  const [mode, setMode] = useState<GameMode>('country');
  const [coverage, setCoverage] = useState<readonly CoverageEntity[] | null>(null);
  const [rivals, setRivals] = useState<readonly Rival[]>([]);
  const [board, setBoard] = useState<readonly BoardRow[]>([]);
  const [window_, setWindow] = useState<'week' | 'all'>('week');
  const [failed, setFailed] = useState(false);

  const deviceToken = identityToken();

  useEffect(() => {
    let live = true;
    setCoverage(null);
    setFailed(false);

    // The board is public; the two personal reads need a device that has
    // played. A browser with no token has no progress to show, which is a
    // different thing from an error.
    const jobs: Promise<unknown>[] = [
      fetchBoard(mode, window_).then((r) => {
        if (live) setBoard(r);
      }),
    ];
    if (deviceToken !== null) {
      jobs.push(
        fetchCoverage(mode, deviceToken).then((r) => {
          if (live) setCoverage(r);
        }),
        fetchRivals(deviceToken).then((r) => {
          if (live) setRivals(r);
        }),
      );
    } else if (live) {
      setCoverage([]);
    }

    void Promise.all(jobs).catch(() => {
      if (live) setFailed(true);
    });
    return () => {
      live = false;
    };
  }, [mode, window_, deviceToken]);

  const found = foundSet(coverage ?? []);
  const totalFound = found.size;
  const totalPossible = BY_REGION.reduce((n, [, codes]) => n + codes.length, 0);

  return (
    <main className="gg-screen gg-scroll">
      <header className="gg-hero">
        <h1 className="gg-title">Your progress</h1>
        <p className="gg-subtitle">
          {account === null
            ? 'Saved on this device. Sign in to keep it.'
            : `Signed in as ${account.email}`}
        </p>
      </header>

      <div className="gg-course-row" role="radiogroup" aria-label="Game mode">
        {GAME_MODES.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mode === m}
            className="gg-chip"
            data-selected={mode === m ? 'true' : 'false'}
            onClick={() => setMode(m)}
          >
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>

      {failed ? <p className="gg-note gg-note-error">Could not load that just now.</p> : null}

      {mode === 'country' ? (
        <section className="gg-section">
          <h2 className="gg-section-title">
            Countries found · {totalFound} of {totalPossible}
          </h2>
          {coverage === null ? (
            <p className="gg-note">Loading…</p>
          ) : totalFound === 0 ? (
            <p className="gg-note">
              Nothing yet. Every country you pin correctly is added here.
            </p>
          ) : (
            <ul className="gg-cover-list">
              {BY_REGION.map(([region, codes]) => {
                const done = codes.filter((c) => found.has(c)).length;
                return (
                  <li key={region} className="gg-cover-row">
                    <span className="gg-cover-name">{region}</span>
                    <Bar done={done} total={codes.length} />
                    <span className="gg-cover-count tabular-nums">
                      {done}/{codes.length}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : (
        <section className="gg-section">
          <h2 className="gg-section-title">Places found</h2>
          <p className="gg-note">
            {coverage === null
              ? 'Loading…'
              : `${found.size} in ${MODE_LABEL[mode]} so far.`}
          </p>
        </section>
      )}

      <section className="gg-section">
        <h2 className="gg-section-title">Leaderboard</h2>
        <div className="gg-course-row" role="radiogroup" aria-label="Board window">
          {(['week', 'all'] as const).map((w) => (
            <button
              key={w}
              type="button"
              role="radio"
              aria-checked={window_ === w}
              className="gg-chip"
              data-selected={window_ === w ? 'true' : 'false'}
              onClick={() => setWindow(w)}
            >
              {w === 'week' ? 'This week' : 'All time'}
            </button>
          ))}
        </div>
        {board.length === 0 ? (
          <p className="gg-note">
            {window_ === 'week'
              ? 'Nobody has scored this week yet. That is an opportunity.'
              : 'No scores yet.'}
          </p>
        ) : (
          <ol className="gg-board">
            {board.map((r) => (
              <li key={`${r.rank}-${r.handle}`} className="gg-board-row">
                <span className="gg-board-rank tabular-nums">{r.rank}</span>
                <span className="gg-board-handle truncate">{r.handle}</span>
                <span className="gg-board-score tabular-nums">{points(r.best)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      {rivals.length > 0 ? (
        <section className="gg-section">
          <h2 className="gg-section-title">Head to head</h2>
          <ul className="gg-board">
            {rivals.map((r) => (
              <li key={r.playerId} className="gg-board-row">
                <span className="gg-board-handle truncate">{r.handle}</span>
                <span className="gg-rival-record tabular-nums">
                  {r.wins}–{r.losses}
                  {r.draws > 0 ? `–${r.draws}` : ''}
                </span>
              </li>
            ))}
          </ul>
          <p className="gg-note">Wins, losses{rivals.some((r) => r.draws > 0) ? ', draws' : ''}.</p>
        </section>
      ) : null}

      <section className="gg-section gg-section-quiet">
        <button type="button" className="gg-btn" onClick={() => navigate({ name: 'home' })}>
          Back to the globe
        </button>
      </section>
    </main>
  );
}
